<template>
  <nav v-if="pageLinks" :aria-label="$t('pageNavigation')">
    <ul class="pagination m-0">
      <li v-if="boundaryLinks" class="page-item" :class="{ disabled: !hasPrevious || disabled }">
        <button
          class="page-link"
          @click="setPage(1)"
          :aria-disabled="evaluate(!hasPrevious || disabled)"
          data-page="first"
        >
          {{ $t('first') }}
        </button>
      </li>
      <li v-if="directionLinks" class="page-item" :class="{ disabled: !hasPrevious || disabled }">
        <button
          class="page-link"
          @click="setPage(page - 1)"
          :aria-disabled="evaluate(!hasPrevious || disabled)"
          data-page="previous"
        >
          {{ $t('previous') }}
        </button>
      </li>

      <li
        v-for="(pageNumber, pi) in pages"
        :key="pi"
        class="page-item"
        :class="{ active: pageNumber === page, disabled: isEllipsis(pageNumber) || disabled }"
        :aria-current="pageNumber === page ? 'page' : null"
      >
        <a
          v-if="isEllipsis(pageNumber)"
          class="page-link"
          :aria-disabled="true"
          data-page="ellipsis"
          >...</a
        >
        <button
          v-if="!isEllipsis(pageNumber)"
          @click="setPage(pageNumber)"
          class="page-link"
          :aria-disabled="evaluate(disabled)"
          :data-page="pageNumber"
        >
          {{ pageNumber }}
          <span v-if="pageNumber === page" class="sr-only">{{ $t('currentAlt') }}</span>
        </button>
      </li>

      <li v-if="directionLinks" class="page-item" :class="{ disabled: !hasNext || disabled }">
        <button
          class="page-link"
          @click="setPage(page + 1)"
          :aria-disabled="evaluate(!hasNext || disabled)"
          data-page="next"
        >
          {{ $t('next') }}
        </button>
      </li>
      <li v-if="boundaryLinks" class="page-item" :class="{ disabled: !hasNext || disabled }">
        <button
          class="page-link"
          @click="setPage(pagesCount)"
          :aria-disabled="evaluate(!hasNext || disabled)"
          data-page="last"
        >
          {{ $t('last') }}
        </button>
      </li>
    </ul>
  </nav>

  <div v-else class="btn-group d-flex pagination">
    <button
      class="btn page-link"
      :class="{ disabled: !hasPrevious || disabled }"
      @click="setPage(page - 1)"
      :aria-disabled="evaluate(!hasPrevious || disabled)"
      data-page="previous"
    >
      {{ $t('previous') }}
    </button>
    <button
      class="btn page-link"
      :class="{ disabled: !hasNext || disabled }"
      @click="setPage(page + 1)"
      :aria-disabled="evaluate(!hasNext || disabled)"
      data-page="next"
    >
      {{ $t('next') }}
    </button>
  </div>
</template>

<script>
import { evaluate } from '@/utils/aria-utils';

export default {
  emits: ['update:modelValue'],
  props: {
    /**
     * Whether to disable buttons from user input
     */
    disabled: {
      type: Boolean,
    },
    /**
     *  Whether to show the "First" and "Last" page links
     */
    boundaryLinks: {
      type: Boolean,
      default: true,
    },
    /**
     *  Whether to show the "Next" and "Previous" page links
     */
    directionLinks: {
      type: Boolean,
      default: true,
    },
    /**
     *  Whether to show the page numbers
     */
    pageLinks: {
      type: Boolean,
      default: true,
    },
    /**
     *  Whether to show ellipsis symbols and first/last page numbers when maxSize > number of pages
     */
    ellipsis: {
      type: Boolean,
      default: true,
    },
    /**
     *  Whether to rotate pages when maxSize > number of pages.
     *  Current page will be in the middle
     */
    rotate: {
      type: Boolean,
      default: true,
    },
    /**
     *  Number of items in collection.
     */
    collectionSize: {
      type: Number,
    },
    /**
     *  Maximum number of pages to display.
     */
    maxSize: {
      type: Number,
      default: 6,
    },
    /**
     *  Current page. Page numbers start with 1
     */
    modelValue: {
      type: Number,
      default: 1,
    },
    /**
     *  Number of items per page.
     */
    pageSize: {
      type: Number,
    },
  },
  methods: {
    evaluate,
    setPage(pageNumber) {
      this.page = pageNumber;
    },
    isEllipsis(pageNumber) {
      return pageNumber === -1;
    },
    /**
     * Paginates page numbers based on maxSize items per page
     */
    applyPagination(value, maxSize) {
      const page = Math.ceil(value / maxSize) - 1;
      const start = page * maxSize;
      const end = start + maxSize;

      return [start, end];
    },
    /**
     * Rotates page numbers based on maxSize items visible.
     * Currently selected page stays in the middle:
     *
     * Ex. for selected page = 6:
     * [5,*6*,7] for maxSize = 3
     * [4,5,*6*,7] for maxSize = 4
     */
    applyRotation(page, pagesCount, maxSize) {
      let start = 0;
      let end = pagesCount;
      const leftOffset = Math.floor(maxSize / 2);
      const rightOffset = maxSize % 2 === 0 ? leftOffset - 1 : leftOffset;

      if (page <= leftOffset) {
        // very beginning, no rotation -> [0..maxSize]
        end = maxSize;
      } else if (pagesCount - page < leftOffset) {
        // very end, no rotation -> [len-maxSize..len]
        start = pagesCount - maxSize;
      } else {
        // rotate
        start = page - leftOffset - 1;
        end = page + rightOffset;
      }

      return [start, end];
    },
  },
  computed: {
    hasPrevious() {
      return this.modelValue > 1;
    },
    hasNext() {
      return this.modelValue < this.pagesCount;
    },
    page: {
      get() {
        return this.modelValue;
      },
      set(page) {
        this.$emit('update:modelValue', Math.max(Math.min(page, this.pagesCount), 1));
      },
    },
    pagesCount() {
      return Math.ceil(this.collectionSize / this.pageSize);
    },
    pages() {
      let newPages = [];

      // fill-in model needed to render pages
      for (let i = 1; i <= this.pagesCount; i += 1) {
        newPages.push(i);
      }

      // apply maxSize if necessary
      if (this.maxSize > 0 && newPages.length > this.maxSize) {
        let start = 0;
        let end = newPages.length;

        // either paginating or rotating page numbers
        if (this.rotate) {
          [start, end] = this.applyRotation(this.page, this.pagesCount, this.maxSize);
        } else {
          [start, end] = this.applyPagination(this.page, this.maxSize);
        }

        newPages = newPages.slice(start, end);

        // Appends ellipsis and first/last page number to the displayed pages
        if (this.ellipsis) {
          if (start > 0) {
            if (start > 1) {
              newPages.unshift(-1);
            }
            newPages.unshift(1);
          }
          if (end < this.pagesCount) {
            if (end < this.pagesCount - 1) {
              newPages.push(-1);
            }
            newPages.push(this.pagesCount);
          }
        }
      }

      return newPages;
    },
  },
};
</script>

<style scoped>
.disabled {
  pointer-events: none;
}
</style>
